import useMembership from "@/shared/hooks/useMembership";
import useGetSmartStoreInfo from "@/features/pathpick/features/basket/hooks/query/useGetSmartStoreInfo";
import useGetNaverPartnerStatus from "@/features/pathpick/features/basket/hooks/query/useGetNaverPartnerStatus";
import useGetNaverPartnerAddressBooks from "@/features/pathpick/features/basket/hooks/query/useGetNaverPartnerAddressBooks";
import useCreatePathPickRequests from "@/features/pathpick/hooks/mutation/useCreatePathPickRequests";
import usePathPickBasketSelection from "@/features/pathpick/features/basket/hooks/usePathPickBasketSelection";
import {
  usePathPickBasketSyncFlowDispatch,
  usePathPickBasketSyncFlowState,
} from "@/features/pathpick/features/basket/contexts/PathPickBasketSyncFlowContext";
import {
  getCurrentSyncFlowStep,
  PathPickSyncIntent,
} from "@/features/pathpick/features/basket/contexts/syncFlowReducer";
import {
  isNaverSyncReady,
  PathPickSyncGateConditions,
  resolveBlockingStep,
} from "@/features/pathpick/features/basket/contexts/resolveBlockingStep";
import { PathPickFileRequestKind } from "@/features/pathpick/features/basket/types";
import {
  PathPickMarketSyncKind,
  PathPickProductRequestKind,
} from "@/features/pathpick/types";
import usePathPickSyncKinds from "@/features/pathpick/hooks/usePathPickSyncKinds";
import { PathPickSmartStoreInfoResponse } from "@/features/pathpick/features/basket/types/response";

// 연동/파일 신청 플로우 — "의도(intent) + 관문(gate)" 구조.
//
// 신청 버튼은 무엇을 하려는지(intent)를 남기고 advance()를 부른다. advance()는 아직 풀리지 않은
// 첫 관문의 모달로 보내고, 그 모달이 해결(저장/연결 확인)에 성공하면 close가 아니라 advance()를
// 다시 불러 남은 관문 또는 종착 단계로 이어간다. 그래서 안내를 읽고 처음부터 다시 신청할 필요가 없다.
//
// 트리거(패널 버튼·플로팅 바)는 여러 곳이지만 상태는 컨텍스트라 공유되고, 모달 렌더는 한 곳(SyncPanel)에서 한다.
const useSyncRequestFlow = () => {
  const flowState = usePathPickBasketSyncFlowState();
  const dispatch = usePathPickBasketSyncFlowDispatch();
  const { intent, blockedStep } = flowState;
  const step = getCurrentSyncFlowStep(flowState);

  const { getRequestableProductsFor, clear } = usePathPickBasketSelection();
  const { canDownload } = useMembership();
  const { smartStoreInfo } = useGetSmartStoreInfo();
  const { naverPartnerStatus } = useGetNaverPartnerStatus();
  const { addressBooks } = useGetNaverPartnerAddressBooks();
  const { mutate: createRequests, isPending: isRequesting } =
    useCreatePathPickRequests();
  // API 연동으로 신청할 수 있는 마켓 — 네이버 + 바이어별 권한이 켜진 마켓(어드민이 켠다).
  // 이 목록이 "이미 연동된 상품" 관문의 판정 기준도 된다.
  const syncKinds = usePathPickSyncKinds();

  // 의도 → 그 의도가 접수하는 요청 종류. 신청 대상 판정(채널별 처리 상태)의 기준이 된다.
  // API 연동은 마켓을 확인 모달에서 고르므로, 고르기 전에는 신청 가능한 마켓 중 하나라도 연동할 수 있으면 통과시킨다.
  const intentRequestKinds = (
    forIntent: PathPickSyncIntent
  ): PathPickProductRequestKind[] =>
    forIntent.kind === "apiSync"
      ? forIntent.markets ?? syncKinds
      : forIntent.formats;

  const conditions = (
    forIntent: PathPickSyncIntent
  ): PathPickSyncGateConditions => ({
    requestableCount: getRequestableProductsFor(intentRequestKinds(forIntent))
      .length,
    smartStoreInfo,
    isNaverPartnerConnected: Boolean(naverPartnerStatus?.connected),
    addressBooks,
    syncKinds,
  });

  // 확인 모달에서 네이버를 기본 선택할지, "기본 정보 설정 필요"를 붙일지의 기준.
  const isNaverReady = isNaverSyncReady({
    smartStoreInfo,
    isNaverPartnerConnected: Boolean(naverPartnerStatus?.connected),
    addressBooks,
  });

  // 고른 마켓으로 API 연동 요청을 접수한다(관문을 모두 통과한 뒤).
  const requestSync = (requestTypes: PathPickMarketSyncKind[]) => {
    createRequests(
      {
        requestTypes,
        // 고른 마켓 중 하나라도 연동 가능한(실패·미신청·연동 해제) 상품만 접수한다 — 이미 연동됐거나 처리 중인 마켓의
        // 스킵은 서버에 맡긴다(파일 신청의 양식 여러 개와 같은 규칙).
        products: getRequestableProductsFor(requestTypes),
      },
      {
        onSuccess: () => {
          clear();
          dispatch({
            type: "GO_TO",
            step: { name: "syncComplete" },
            intent: null,
            mode: "reset",
          });
        },
      }
    );
  };

  // 파일 신청 모달에서 고른 양식(NAVER_EXCEL / KREAM_EXCEL)을 그대로 요청 종류로 접수한다.
  const requestDownload = (formats: PathPickFileRequestKind[]) => {
    createRequests(
      {
        requestTypes: formats,
        // 이미 완료됐거나 처리 중인 상품은 접수하지 않는다 — 실패·미신청·삭제(연동 해제) 상품만 재신청 대상이다.
        products: getRequestableProductsFor(formats),
      },
      {
        onSuccess: () => {
          clear();
          dispatch({
            type: "GO_TO",
            step: { name: "downloadComplete" },
            intent: null,
            mode: "reset",
          });
        },
      }
    );
  };

  // 관문을 다시 평가해 다음 단계로 넘어간다.
  // overrides — 저장/재조회 직후에는 캐시 반영을 기다리지 않고 응답 값으로 즉시 판정한다.
  const advance = (
    nextIntent: PathPickSyncIntent,
    overrides?: Partial<PathPickSyncGateConditions>
  ) => {
    const blocking = resolveBlockingStep(nextIntent, {
      ...conditions(nextIntent),
      ...overrides,
    });

    // 관문을 모두 통과 — API 연동은 마켓을 고르기 전이면 확인 모달(마켓 선택)로, 고른 뒤면 바로 접수한다.
    // 파일 신청은 양식 선택에서 이미 확인을 받았으므로 바로 접수한다.
    if (!blocking) {
      if (nextIntent.kind === "apiSync" && !nextIntent.markets) {
        dispatch({
          type: "GO_TO",
          step: { name: "syncConfirm" },
          intent: nextIntent,
          mode: "replace",
        });
      } else if (nextIntent.kind === "apiSync") {
        requestSync(nextIntent.markets ?? []);
      } else {
        requestDownload(nextIntent.formats);
      }
      return;
    }

    // 방금 해결하려던 관문이 그대로 남아 있으면(예: 주소록에 해외 출고지가 아예 없는 상태로 저장)
    // 모달을 다시 띄우지 않고 그 자리에서 알린다 — 무한 재오픈을 막는 가드다.
    if (blocking.name === step?.name) {
      dispatch({ type: "BLOCK", step: blocking });
      return;
    }

    dispatch({
      type: "GO_TO",
      step: blocking,
      intent: nextIntent,
      // 파일 신청과 마켓을 고른 뒤의 API 연동(네이버를 골랐는데 기본 정보가 덜 된 경우)은 관문에서 취소하면
      // 양식·마켓 선택으로 돌아가야 하므로 쌓고, 마켓을 고르기 전 API 연동은 취소 = 플로우 종료라 갈아끼운다.
      mode:
        nextIntent.kind === "fileRequest" || nextIntent.markets
          ? "push"
          : "replace",
    });
  };

  // API 연동 신청 — 중복 연동 관문 뒤 마켓 선택으로 간다. 네이버 기본 정보·제휴 연결·해외 출고지 관문은
  // 네이버를 고를 때만 거친다(고를 마켓이 네이버뿐이면 지금처럼 마켓 선택 전에).
  const startApiSync = () => advance({ kind: "apiSync" });

  // 파일 신청 — 어떤 양식이 필요한지 정해져야 관문(기본 정보) 필요 여부를 알 수 있어 양식 선택부터 띄운다.
  const startFileRequest = () => {
    // 네이버+크림 다운로드 권한이 모두 있어야 파일 신청이 가능하다
    if (!canDownload) {
      return;
    }
    dispatch({
      type: "GO_TO",
      step: { name: "fileFormatSelect" },
      intent: null,
      mode: "reset",
    });
  };

  // 양식 선택 모달의 신청 — 여기서 비로소 의도가 확정되고 관문 검사가 시작된다.
  const submitFileRequest = (formats: PathPickFileRequestKind[]) =>
    advance({ kind: "fileRequest", formats });

  // 패널의 [기본 정보 설정] 버튼 — 신청 의도 없이 단독으로 여는 경로.
  const openBuyerInfo = () =>
    dispatch({
      type: "GO_TO",
      step: { name: "buyerInfo", variant: "standalone" },
      intent: null,
      mode: "reset",
    });

  // 기본 정보 저장 성공 — 진행 중인 신청이 있으면 저장된 정보로 관문을 다시 판정해 이어간다.
  const handleBuyerInfoSaved = (saved: PathPickSmartStoreInfoResponse) => {
    if (!intent) {
      dispatch({ type: "CLOSE" });
      return;
    }
    advance(intent, { smartStoreInfo: saved });
  };

  // 확인 모달에서 마켓을 골랐다 — 네이버를 골랐으면 네이버 관문을 거쳐, 아니면 바로 접수한다.
  // 마켓 선택이 없는 바이어는 모달이 ["NAVER_SYNC"]만 넘긴다(관문은 이미 통과했다).
  const confirmSync = (requestTypes: PathPickMarketSyncKind[]) =>
    advance({ kind: "apiSync", markets: requestTypes });

  // 취소/닫기는 한 단계 뒤로 — 되돌아갈 단계가 없으면 플로우 전체가 닫힌다.
  // (파일 신청 관문만 스택을 쌓으므로 결과적으로 파일 신청에서만 이전 모달로 복귀한다)
  const dismiss = () => dispatch({ type: "BACK" });

  return {
    step,
    intent,
    blockedStep,
    isRequesting,
    smartStoreInfo,
    syncKinds,
    isNaverReady,
    startApiSync,
    startFileRequest,
    submitFileRequest,
    openBuyerInfo,
    handleBuyerInfoSaved,
    confirmSync,
    dismiss,
  };
};

export default useSyncRequestFlow;
